/**
 * PageList — the pages of this report, added one at a time (ADR 0015, 0016).
 *
 * The list starts empty. "Add a page" offers the chosen FORMAT's own pages
 * first, in the template's order (formats.js): each fills itself, or asks for
 * the meshes and carries the template's caption presets, or says it is made in
 * GIMP. Under them, the pages that work in any format — six views, a single
 * view, print models, CT data, the text pages — and any of the format's
 * recipes no template page uses.
 *
 * Each page can be previewed or exported on its own, and the report is the
 * pages in this order. Pure presentation: the list lives in usePageBasket and
 * every change is a basket.js operation; captions are the page's edits, the
 * same store the page editor writes, so a caption lives in one place.
 */
import { useState } from 'react'

import { ROLE_LABELS } from '../../lib/caseReport/classify.js'
import { newEdit } from '../../lib/caseReport/edits.js'
import { SINGLE_VIEWS } from '../../lib/caseReport/genericPages.js'
import {
  addPage, removePage, movePage, duplicatePage, setPagePick, setPageInputs, removePageFile,
} from '../../lib/caseReport/basket.js'

const btn = 'rounded-md px-2 py-1 text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed'
const sel = 'border border-gray-300 rounded px-1.5 py-0.5 text-xs bg-white max-w-[16rem]'
const field = 'border border-gray-300 rounded px-2 py-1 text-xs w-full'

/** What each generic type asks of the operator, in one line. */
const HINT = {
  'g-six-views': 'pick the meshes — six standard views',
  'g-view-row': 'pick the meshes — four views in a row, or the ones a template page states',
  'g-single-view': 'pick the meshes and one view — fills the page',
  'g-print-models': 'pick the models — one picture each',
  'g-colour-map': 'give the ZBrush colour-map image and type the legend',
  'g-ct-data': 'filled from the portal by Case ID',
  'g-agreement': 'the standard agreement',
  'g-terms-1': 'the standard terms, page 1',
  'g-terms-2': 'the standard terms, page 2',
}

const VIEW_NAMES = {
  A: 'Anterior', P: 'Posterior', LL: 'Left lateral', RL: 'Right lateral',
  S: 'Superior', I: 'Inferior', ISO: 'Isometric',
}

/** A slot key as the operator reads it: "Skull (native)". */
function slotLabel(key) {
  const [role, stage] = key.split('/')
  return `${ROLE_LABELS[role] ?? role} (${stage === '*' ? 'any stage' : stage})`
}

export default function PageList({
  basket, offered, draft, library, availability, apply, startFromUsual, clear, onAddFiles,
  onAddImage, liveAssets = null,
  edits, setEdits, onPreview, onExport, busy,
}) {
  const [refusal, setRefusal] = useState(null)
  const [open, setOpen] = useState({})
  if (!basket) return null

  const run = (op) => setRefusal(apply(op))
  const types = [...offered.template, ...offered.generic, ...offered.other]
  const typeOf = new Map(types.map((t) => [t.id, t]))
  const perPage = new Map((draft?.pages ?? []).map((p) => [p.instanceId, p]))
  const notesFor = (id) => (draft?.notes ?? []).filter((n) => n.instanceId === id)
  const firstSheet = (id) => draft?.doc?.pages?.find((p) => p.pageInstanceId === id)

  const captionOf = (id, slot) => edits[`pageinst:${id}`]?.captions?.[slot]
  // An emptied box goes back to the page's own caption, which its placeholder
  // shows. Stored as '', it would print a blank caption under a box that reads
  // as the default. Blanking a caption on purpose is the page editor's job.
  const setCaption = (id, slot, value) => setEdits((prev) => {
    const key = `pageinst:${id}`
    const cur = prev[key] ?? newEdit()
    const captions = { ...cur.captions }
    if (value === '') delete captions[slot]
    else captions[slot] = value
    return { ...prev, [key]: { ...cur, captions } }
  })

  const toggleMesh = (e, meshId) => {
    const cur = e.inputs?.meshes ?? []
    const removing = cur.includes(meshId)
    const next = removing ? cur.filter((x) => x !== meshId) : [...cur, meshId]
    // Pin the list open. It opens by itself while nothing is picked, so without
    // this the FIRST tick made "nothing picked" false and the list shut under
    // the operator's hand — found by the page-mode harness.
    setOpen((o) => ({ ...o, [`${e.instanceId}:files`]: true }))
    // Removing goes through removePageFile, which drops any slot pick naming
    // that file in the same operation — see basket.js for why they must move
    // together.
    run((b) => (removing
      ? removePageFile(b, e.instanceId, meshId)
      : setPageInputs(b, e.instanceId, { meshes: next })))
  }

  /**
   * Where a template page goes when it is added: at its own place among the
   * template pages already in the list, so the report keeps the template's
   * order however the operator builds it up.
   */
  const templateOrder = new Map(offered.template.map((t, i) => [t.id, i]))
  const positionFor = (t) => {
    const mine = templateOrder.get(t.id)
    if (mine == null) return undefined                   // a generic page goes at the end
    let at = basket.entries.length
    for (const [i, e] of basket.entries.entries()) {
      const other = templateOrder.get(e.typeId)
      if (other != null && other > mine) { at = i; break }
    }
    return at
  }

  const addRow = (t, extra, n = null) => {
    const present = basket.entries.some((e) => e.typeId === t.id)
    const once = (!t.repeatable && present) || !!t.hand
    return (
      <div key={t.id} className="flex items-center gap-2 text-xs">
        <button type="button" className={`${btn} border border-gray-300 shrink-0`} disabled={once}
          data-testid={`page-add-${t.id}`}
          title={t.hand ? 'Made in GIMP'
            : once ? 'Already in the report — this page can appear only once'
              : 'Add at this page\'s place in the template'}
          onClick={() => run((b) => addPage(b, t.id, { at: positionFor(t) }))}>Add</button>
        {n != null && <span className="w-5 shrink-0 text-right text-gray-400">{n}</span>}
        <span className={`w-48 shrink-0 ${once ? 'text-gray-400' : 'text-gray-700'}`}>
          {t.listTitle ?? t.title}
        </span>
        <span className="text-gray-500">
          {t.hand ? `made in GIMP — ${t.hand}` : (present && !t.repeatable) ? 'already in the report' : extra}
        </span>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="page-list">
      <div className="flex items-center justify-between mb-1 gap-2">
        <span className="text-sm font-semibold text-gray-700">
          Pages in this report ({basket.entries.length})
        </span>
        <div className="flex gap-1">
          <button type="button" className={`${btn} border border-gray-300 text-gray-700`}
            onClick={() => { startFromUsual(); setRefusal(null) }} data-testid="page-list-usual"
            title="Add every page of this template that the Builder can build, in order">
            Add every page of this template
          </button>
          <button type="button" className={`${btn} border border-gray-300 text-gray-700`}
            disabled={!basket.entries.length}
            onClick={() => { clear(); setRefusal(null) }} data-testid="page-list-clear">
            Clear
          </button>
        </div>
      </div>

      {refusal && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 my-2"
          data-testid="page-list-refusal">
          {refusal}
        </p>
      )}

      {!basket.entries.length && (
        <p className="text-xs text-gray-500 my-3" data-testid="page-list-empty">
          No pages yet. Add the pages this case needs — each can be previewed or exported on
          its own, and Build report joins them in the order below.
        </p>
      )}

      <ol className="space-y-2 mt-2">
        {basket.entries.map((e, i) => {
          const type = typeOf.get(e.typeId)
          const info = perPage.get(e.instanceId)
          const notes = notesFor(e.instanceId)
          const built = info?.built ?? 0
          const skipped = notes.find((n) => n.kind === 'skipped')
          const incomplete = notes.filter((n) => n.kind === 'incomplete')
          const wants = type?.inputs ?? []
          // Every page that shows models asks for its own files. Text pages —
          // the agreement, the terms — show none and ask for nothing.
          //
          // A page made in GIMP asks for files too, when its template variant
          // lays out renders (`sheet`): the team draws the dimensions ON those
          // renders, so the working sheet has to carry them. A hand page with no
          // `sheet` is text, and still asks for nothing.
          // A page GIVEN its picture — the colour map — shows no mesh at all.
          //
          // An `info` page asks for files only when it DECLARES a mesh slot. Excluding the
          // block outright hid the picker on the source-data page, so a page that declared the
          // slot had no way to fill it. Team, 5 Oct 2026: "There is no option to add OBJ files
          // for generating images on the Preview Image page."
          //
          // Gated on the block and not on `inputs` alone: a template page resolves its meshes
          // from the library by role and declares no inputs at all, so asking `inputs` alone
          // would have taken the picker off 30 of them — measured across all 99 offered types
          // before this line was written.
          const needsFiles = (!type?.hand || !!type?.sheet)
            && type?.block !== 'text'
            && (type?.block !== 'info' || wants.includes('meshes'))
            && !wants.includes('image')
          const chosen = e.inputs?.meshes ?? []
          const slots = wants.length ? [] : (info?.slots ?? [])
          // The reuse list stays shut until asked for: the page's own files are
          // the normal way in, and this is the shortcut for a file already used.
          // Two disclosures live in this row — the reuse list and the slot
          // picks — so they are keyed apart; one toggle used to move both.
          const showMeshes = open[`${e.instanceId}:files`] ?? false
          const sheet = firstSheet(e.instanceId)
          const hasCaptions = type && type.block !== 'text' && type.block !== 'info'
          return (
            <li key={e.instanceId} className="border border-gray-200 rounded-lg p-2"
              data-testid={`page-row-${e.instanceId}`}>
              <div className="flex items-start gap-2">
                <span className="text-xs text-gray-400 w-5 shrink-0 pt-1">{i + 1}.</span>
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-gray-800 font-medium">
                    {type?.listTitle ?? type?.title ?? e.typeId}
                  </span>
                  {built > 1 && <span className="text-xs text-gray-500"> — {built} sheets</span>}
                  {skipped && (
                    <span className="block text-xs text-gray-500">
                      Not built — {skipped.reason.replace(/^not built — /, '')}
                    </span>
                  )}
                  {incomplete.map((n, k) => (
                    <span key={k} className="block text-xs text-amber-800">{n.reason}</span>
                  ))}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button type="button" className={`${btn} border border-gray-300`} disabled={i === 0}
                    title="Move up" data-testid={`page-up-${e.instanceId}`}
                    onClick={() => run((b) => movePage(b, e.instanceId, i - 1))}>▲</button>
                  <button type="button" className={`${btn} border border-gray-300`}
                    disabled={i === basket.entries.length - 1}
                    title="Move down" data-testid={`page-down-${e.instanceId}`}
                    onClick={() => run((b) => movePage(b, e.instanceId, i + 1))}>▼</button>
                  {type?.repeatable && (
                    <button type="button" className={`${btn} border border-gray-300`}
                      title="Add another page like this one, right after it"
                      data-testid={`page-dup-${e.instanceId}`}
                      onClick={() => run((b) => duplicatePage(b, e.instanceId))}>Copy</button>
                  )}
                  <button type="button" className={`${btn} border border-gray-300 text-red-700`}
                    title="Remove this page from the report" data-testid={`page-remove-${e.instanceId}`}
                    onClick={() => run((b) => removePage(b, e.instanceId))}>Remove</button>
                  {/* A page made in GIMP has nothing BUILT, and still downloads:
                      a working sheet carrying the chrome, the heading and its
                      renders, for the team to draw on. That is why these are not
                      gated on `built` alone. */}
                  <button type="button" className={`${btn} border border-brand-600 text-brand-700`}
                    disabled={(!built && !(type?.hand && type?.sheet)) || !!busy}
                    title="See this page on its own"
                    data-testid={`page-preview-${e.instanceId}`}
                    onClick={() => onPreview(e.instanceId)}>Preview</button>
                  {/* A hand page with no `sheet` (a leaflet, a QR page) has no working sheet to
                      draw on, so its buttons stay off rather than ending in an error (G3, 28 Sep). */}
                  <button type="button" className={`${btn} bg-brand-600 text-white`}
                    disabled={(!built && !(type?.hand && type?.sheet)) || !!busy}
                    title={type?.hand
                      ? 'Download this page to draw on in GIMP — the report’s own header, frame and footer, with the drawing area empty'
                      : 'This page on its own, without a page number, to merge by hand'}
                    data-testid={`page-export-${e.instanceId}`}
                    onClick={() => onExport(e.instanceId)}>
                    {type?.hand ? 'Download to draw' : 'Export'}
                  </button>
                </div>
              </div>

              <div className="ml-7 mt-1 space-y-1">
                {needsFiles && (
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <label className={`${btn} border border-gray-300 cursor-pointer`}>
                        Choose files…
                        <input type="file" multiple accept=".stl,.obj,.STL,.OBJ" className="hidden"
                          data-testid={`page-files-${e.instanceId}`}
                          onChange={(ev) => { onAddFiles?.(e.instanceId, ev.target.files); ev.target.value = '' }} />
                      </label>
                      <span className="text-xs text-gray-500">
                        {chosen.length
                          ? `${chosen.length} file${chosen.length === 1 ? '' : 's'} on this page`
                          : 'the STL / OBJ files this page shows'}
                      </span>
                    </div>

                    {/* What this page shows, with the role each file was read as
                        — the colour follows that role, so a wrong read is worth
                        seeing here rather than on the finished page. */}
                    {!!chosen.length && (
                      <div className="mt-1 grid grid-cols-1 gap-0.5">
                        {chosen.map((id) => {
                          const m = library.find((x) => x.id === id)
                          return (
                            <div key={id} className="flex items-center gap-2 text-xs">
                              <button type="button" className="text-red-700"
                                title="Take this file off the page"
                                data-testid={`page-file-remove-${e.instanceId}-${id}`}
                                onClick={() => toggleMesh(e, id)}>×</button>
                              <span className="w-40 shrink-0 text-gray-500">
                                {m ? (ROLE_LABELS[m.role] ?? m.role) : 'missing'}
                              </span>
                              <span className="truncate text-gray-700">{m?.name ?? id}</span>
                            </div>
                          )
                        })}
                      </div>
                    )}

                    {/* A file already in this report — the skull is chosen once
                        and used on every page that shows it. */}
                    {library.some((m) => !chosen.includes(m.id)) && (
                      <div className="mt-1">
                        <button type="button" className="text-xs text-brand-700 underline"
                          data-testid={`page-meshes-${e.instanceId}`}
                          onClick={() => setOpen((o) => ({ ...o, [`${e.instanceId}:files`]: !showMeshes }))}>
                          {showMeshes ? 'Hide' : 'Use a file already in this report'}
                        </button>
                        {showMeshes && (
                          <div className="mt-1 grid grid-cols-1 gap-0.5">
                            {library.filter((m) => !chosen.includes(m.id)).map((m) => (
                              <label key={m.id} className="flex items-center gap-2 text-xs">
                                <input type="checkbox" checked={false}
                                  data-testid={`page-mesh-${e.instanceId}-${m.id}`}
                                  onChange={() => toggleMesh(e, m.id)} />
                                <span className="w-40 shrink-0 text-gray-500">{ROLE_LABELS[m.role] ?? m.role}</span>
                                <span className="truncate text-gray-700">{m.name}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* The colour map's picture. The bytes stay in this session only —
                    the list remembers the file's name, and after a reload the row
                    says the picture is needed again rather than drawing nothing. */}
                {wants.includes('image') && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <label className={`${btn} border border-gray-300 cursor-pointer`}>
                      Choose image…
                      <input type="file" accept="image/png,image/jpeg,.png,.jpg,.jpeg" className="hidden"
                        data-testid={`page-image-${e.instanceId}`}
                        onChange={(ev) => { onAddImage?.(e.instanceId, ev.target.files?.[0]); ev.target.value = '' }} />
                    </label>
                    <span className="text-xs text-gray-500">
                      {!e.inputs?.image ? 'the colour-map render (PNG or JPEG); a black background is removed'
                        : liveAssets && !liveAssets.has(e.inputs.image)
                          ? `${e.inputs.imageName ?? 'image'} — choose it again, the picture is not kept between visits`
                          : e.inputs.imageName ?? 'image on this page'}
                    </span>
                  </div>
                )}
                {wants.includes('legend') && (
                  <label className="block text-xs">
                    <span className="text-gray-600">Legend — one row per line: colour, then its label</span>
                    <textarea className={`${field} font-mono`} rows={3}
                      placeholder={'#ff0000 0.10 mm\n#00c800 0.80 mm\n#0000ff 1.50 mm'}
                      value={e.inputs?.legend ?? ''}
                      data-testid={`page-legend-${e.instanceId}`}
                      onChange={(ev) => run((b) => setPageInputs(b, e.instanceId, { legend: ev.target.value }))} />
                  </label>
                )}

                {wants.includes('view') && (
                  <label className="flex items-center gap-2 text-xs">
                    <span className="w-16 text-gray-600">View</span>
                    <select className={sel} value={e.inputs?.view ?? 'A'}
                      data-testid={`page-view-${e.instanceId}`}
                      onChange={(ev) => run((b) => setPageInputs(b, e.instanceId, { view: ev.target.value }))}>
                      {SINGLE_VIEWS.map((v) => <option key={v} value={v}>{VIEW_NAMES[v] ?? v}</option>)}
                    </select>
                  </label>
                )}

                {/* A suggested page's mesh choice: its own slots, "Detected" by default. */}
                {!!slots.length && (
                  <div>
                    <button type="button" className="text-xs text-brand-700 underline"
                      data-testid={`page-slots-${e.instanceId}`}
                      onClick={() => setOpen((o) => ({ ...o, [`${e.instanceId}:slots`]: !o[`${e.instanceId}:slots`] }))}>
                      {open[`${e.instanceId}:slots`] ? 'Hide slots' : 'Which file fills which slot'}
                      {Object.keys(e.picks ?? {}).length ? ` (${Object.keys(e.picks).length} chosen by you)` : ''}
                    </button>
                    {open[`${e.instanceId}:slots`] && slots.map((key) => (
                      <label key={key} className="flex items-center gap-2 text-xs mt-1">
                        <span className="w-44 shrink-0 text-gray-600">{slotLabel(key)}</span>
                        <select className={sel} value={e.picks?.[key] ?? ''}
                          data-testid={`page-slot-${e.instanceId}-${key}`}
                          onChange={(ev) => run((b) => setPagePick(b, e.instanceId, key, ev.target.value || null))}>
                          <option value="">↺ Detected automatically</option>
                          {library.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                      </label>
                    ))}
                  </div>
                )}

                {hasCaptions && (
                  <div className="grid grid-cols-2 gap-2">
                    {[['tc', 'Top caption'], ['bc', 'Bottom caption']].map(([slot, label]) => (
                      <label key={slot} className="text-xs text-gray-600">
                        {label}
                        <input className={field} value={captionOf(e.instanceId, slot) ?? ''}
                          placeholder={sheet?.captions?.[slot] || ''}
                          data-testid={`page-caption-${e.instanceId}-${slot}`}
                          onChange={(ev) => setCaption(e.instanceId, slot, ev.target.value)} />
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </li>
          )
        })}
      </ol>

      <div className="mt-4">
        <span className="text-xs font-semibold text-gray-600 block mb-1">Pages of this template</span>
        <div className="space-y-1" data-testid="page-add-template">
          {offered.template.map((t, i) => {
            const a = availability?.[t.id]
            const hint = t.hand ? '' : t.genericId ? (HINT[t.genericId] ?? '')
              : a?.ok ? 'this case can fill it' : (a?.reason ?? '')
            return addRow(t, hint, i + 1)
          })}
        </div>

        <span className="text-xs font-semibold text-gray-600 block mt-3 mb-1">Other pages</span>
        <div className="space-y-1" data-testid="page-add-other">
          {offered.generic.map((t) => addRow(t, HINT[t.id] ?? ''))}
          {offered.other.map((t) => {
            const a = availability?.[t.id]
            return addRow(t, offered.held ? `held — ${offered.held}`
              : a?.ok ? 'this case can fill it' : (a?.reason ?? ''))
          })}
        </div>
      </div>
    </div>
  )
}
